import { useCallback, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'

import useIsMobile from '@/hooks/layout/useIsMobile'
import { useApi } from '@/hooks/useApi'
import { useAllConversationsStore } from '@/store/useAllConversationsStore'
import { useCurrentConversationStore } from '@/store/useCurrentConversationStore'
import { useUIStore } from '@/store/useUIStore'

import useProjectStore from '@/store/useProjectStore'
import GradientWrapper from './GradientWrapper'
import ResponsiveSidebar from './ResponsiveSidebar'
import Shell from './Shell'

/**
 * Renders the main layout component for the app.
 *
 * Coordinates sidebar state and loading the current conversation.
 * Consists of a fullscreen layout with a sidebar and a main content area.
 * @returns {JSX.Element}  The layout component
 */
const Layout = () => {
  const { isLoading: authLoading, isAuthenticated } = useAuth0()

  const isMobile = useIsMobile()
  const storeIsMobile = useUIStore(s => s.isMobile)
  const setIsMobile = useUIStore(s => s.setIsMobile)
  const setSidebarOpen = useUIStore(s => s.setSidebarOpen)

  useEffect(() => {
    if (isMobile === undefined) return
    if (setIsMobile === undefined) return
    if (storeIsMobile === undefined && isMobile !== undefined) {
      setIsMobile(isMobile)
      return
    }
    if (storeIsMobile === isMobile) return
    setSidebarOpen(!isMobile)
    setIsMobile(isMobile)
  }, [isMobile, setIsMobile, storeIsMobile, setSidebarOpen])

  const api = useApi()
  const loadConversations = useAllConversationsStore(state => state.loadConversations)
  const setToastMessage = useCurrentConversationStore(state => state.setToastMessage)
  const navigate = useNavigate()

  const onError = useCallback(
    err => {
      setToastMessage({
        title: `${err.name}`,
        message: err.message,
        type: 'destructive',
      })
    },
    [setToastMessage],
  )

  // Only hit authenticated endpoints once Auth0 finished loading + user is authenticated
  useEffect(() => {
    if (authLoading) return
    if (!isAuthenticated) return
    if (!api) return

    loadConversations(api, 15, onError)
  }, [authLoading, isAuthenticated, loadConversations, api, onError])

  const { conversationId: rawId } = useParams()
  const urlConvId = rawId ? Number(rawId) : null

  const loadConversation = useCurrentConversationStore(state => state.loadConversation)
  const fetchProjects = useProjectStore(state => state.fetchProjects)
  const clearConversation = useCurrentConversationStore(state => state.clearCurrentConversation)
  const storeConvId = useCurrentConversationStore(s => s.conversationId)

  // IMPORTANT:
  // Use separate AbortControllers for conversation loading and project loading.
  const conversationAbortRef = useRef()
  const projectsAbortRef = useRef()

  // prevent loading a conversation that was just streamed or is streaming
  const streamPhase = useCurrentConversationStore(s => s.streamPhase)
  const isStreaming = streamPhase === 'streaming' || streamPhase === 'loading'

  useEffect(() => {
    if (authLoading) return
    if (!isAuthenticated) return
    if (!api) return
    if (isStreaming) return

    conversationAbortRef?.current?.abort()

    if (!urlConvId) {
      clearConversation()
      return
    }

    const controller = new AbortController()
    conversationAbortRef.current = controller

    ;(async () => {
      try {
        await loadConversation(api, urlConvId, {}, { signal: controller.signal })
      } catch (err) {
        if (err?.status === 404) {
          clearConversation()
          console.warn('[Layout]Conversation 404 Redirect')
          navigate('/chat', { replace: true })
        } else {
          console.error('Failed to load conversation', err)
        }
      }
    })()

    return () => controller.abort()
  }, [
    authLoading,
    isAuthenticated,
    api,
    urlConvId,
    storeConvId,
    navigate,
    loadConversation,
    clearConversation,
    isStreaming,
  ])

  useEffect(() => {
    if (authLoading) return
    if (!isAuthenticated) return
    if (!api) return
    if (isStreaming) return

    projectsAbortRef?.current?.abort()

    const controller = new AbortController()
    projectsAbortRef.current = controller

    ;(async () => {
      try {
        await fetchProjects(api, {}, { signal: controller.signal })
      } catch (err) {
        console.error('Failed to load projects', err)
      }
    })()

    return () => controller.abort()
  }, [authLoading, isAuthenticated, api, fetchProjects, isStreaming])

  return (
    <GradientWrapper>
      <div
        id="fullscreen-layout-outer-wrapper"
        className="fullscreen-container fixed inset-0 flex flex-col overflow-auto"
      >
        <div
          id="fullscreen-layout-inner-wrapper"
          className="relative z-0 flex size-full flex-1 transition-colors"
        >
          <div id="fullscreen-layout-two-columns" className="relative flex size-full flex-row">
            <ResponsiveSidebar />
            <Shell />
          </div>
        </div>
      </div>
      <span className="pointer-events-none fixed inset-0 z-[60] mx-auto my-2 flex max-w-[560px] flex-col items-stretch justify-start md:pb-5" />
    </GradientWrapper>
  )
}

export default Layout
